import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PracticeChallenge } from "@/components/PracticeChallenge";
import { Insight } from "@/components/Insight";
import { Keyboard } from "@/components/Keyboard";
import {
  Tip, KeyIdea, Pull, Steps, Step, Vocab, Term, NextUp,
  KeyHighlights, Highlight,
} from "@/components/Callouts";

export default function PianoFoundation22() {
  const { lesson, course, accent } = findLesson("piano-foundation", "22");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-foundation"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyHighlights>
        <Highlight>The third is the most important interval on the piano — and in all music. Build it by taking any scale note and <strong>skipping one</strong>.</Highlight>
        <Highlight>In <strong>A major</strong>: A's third is C♯. B's third is D. C♯'s third is E. D's third is F♯. And so on around the scale.</Highlight>
        <Highlight>Two flavours of the same exercise: <strong>close thirds</strong> (third above the root) and <strong>spread thirds</strong> (third below the root, played with index + pinky).</Highlight>
        <Highlight>Use the same exercise on <strong>any scale</strong> — A major, A harmonic minor, anything. Just adapt the third to the scale's notes.</Highlight>
        <Highlight>Don't end with up-and-down. Make a <strong>rhythm pattern</strong>, build a riff, put your own stamp on the thirds.</Highlight>
      </KeyHighlights>

      <Insight eyebrow="Why thirds matter" title="The interval at the heart of every chord" accent={accent.hex}>
        <Pull>
          In this lesson, we are going to look at the most important interval on the
          piano and obviously in music. It's called as the thirds. Okay, so let's first
          learn how to build them and then we'll pretty much just play them on the piano
          and get acquainted with them.
        </Pull>

        <p>
          Every triad is two thirds stacked on top of each other. Every chord progression
          you play is a sequence of thirds reorganising. Drilling thirds builds the muscle
          memory and ear training that the rest of harmony rests on.
        </p>

        <Vocab>
          <Term name="Third (interval)" def="the distance between a scale note and the note two scale steps higher. Skip one to find a third." />
          <Term name="Close third" def="the third played above the root — A then C♯ in A major." />
          <Term name="Spread third" def="the third played below the root — C♯ then A. Use index then pinky." />
          <Term name="Diatonic third" def="a third that stays inside the scale. The interval can be major or minor depending on the scale notes." />
        </Vocab>
      </Insight>

      <Insight eyebrow="Build them on A major" title="Skip one scale step at a time" accent={accent.hex}>
        <p>
          A major has 7 notes: <strong>A, B, C♯, D, E, F♯, G♯</strong>. Walking up the scale
          and pairing each note with its third:
        </p>

        <div className="overflow-x-auto rounded-xl border border-nsm-border bg-nsm-card/50 p-4 text-[14px]">
          <table className="w-full">
            <thead>
              <tr className="text-nsm-dim">
                <th className="text-left">Root</th>
                <th>A</th><th>B</th><th>C♯</th><th>D</th><th>E</th><th>F♯</th><th>G♯</th><th>A</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              <tr className="border-t border-nsm-border">
                <td className="text-amber-300">Third</td>
                <td>C♯</td><td>D</td><td>E</td><td>F♯</td><td>G♯</td><td>A</td><td>B</td><td>C♯</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Tip>
          Write this out before you play it — the table on paper, then on the keyboard.
          The writing-down step is non-negotiable: <em>"Before you even attempt to
          play it on the piano, I'd encourage you to write it down."</em>
        </Tip>
      </Insight>

      <Insight eyebrow="The first drill" title="Close thirds — ascending and descending" accent={accent.hex}>
        <p>
          Play each third with the third on top — A, then C♯; B, then D; C♯, then E; and so
          on up the scale. Then come back down: G♯ then E; F♯ then D; E then C♯…
        </p>

        <Tip>
          Use only three fingers when ascending: <strong>thumb, middle, pinky</strong> (or
          equivalent). Avoiding the index and ring fingers prevents your hand from getting
          "trapped" when it runs out of fingers mid-scale.
        </Tip>

        <Keyboard
          startOctave={3}
          octaves={2}
          highlights={[
            { pitch: "A4", color: "#22c55e", label: "A" },
            { pitch: "C#5", color: "#3b82f6", label: "+3" },
            { pitch: "B4", color: "#22c55e", label: "B" },
            { pitch: "D5", color: "#3b82f6", label: "+3" },
            { pitch: "E4", color: "#22c55e", label: "E" },
            { pitch: "G#4", color: "#3b82f6", label: "+3" },
          ]}
          height={180}
        />

        <PracticeChallenge
          courseSlug="piano-foundation"
          order="22"
          id="thirds-amajor"
          mode="sequence"
          title="Diatonic thirds in A major"
          prompt="Walk the thirds up A major: A–C♯, B–D, C♯–E, D–F♯, E–G♯."
          notes={["A4", "C#5", "B4", "D5", "C#5", "E5", "D5", "F#5", "E5", "G#5"]}
          startOctave={4}
          octaves={2}
        />
      </Insight>

      <Insight eyebrow="The second drill" title="Spread thirds — the third below" accent={accent.hex}>
        <p>
          Same pairs of notes, opposite direction. Now the third is <em>below</em> the root.
          A's spread third = C♯ <em>below</em> A. Use <strong>index for the upper note,
          pinky for the lower</strong>. The hand stretches across an octave instead of a
          third.
        </p>

        <p className="font-mono text-[14px] text-amber-300">
          Up: C♯ A · D B · E C♯ · F♯ D · …
        </p>

        <Pull>
          The other one I played for you earlier was closed thirds. But what you'll also
          use in music very often are spread thirds.
        </Pull>
      </Insight>

      <Insight eyebrow="Same exercise, different scale" title="Try A harmonic minor next" accent={accent.hex}>
        <p>
          Once A major thirds feel solid, swap the scale for <strong>A harmonic minor</strong>:
          A, B, C, D, E, F, G♯, A. Build the same thirds on the new scale notes — A's third
          is now C (not C♯). B's third is D. C's third is E. D's third is F. And so on.
        </p>

        <p>
          The fingering is identical. Only the keys under your hand have changed. That
          transferability is the whole reason the exercise matters — once thirds are in your
          ear, you can find them in any scale within seconds.
        </p>
      </Insight>

      <Insight eyebrow="Put your own stamp on it" title="Don't just go up and down" accent={accent.hex}>
        <Pull>
          Rather than ending the puzzle with just going up and down, I would really like
          you to put a stamp on the exercise and create a melody or create a pattern of
          your own using the concept of thirds.
        </Pull>

        <Steps>
          <Step>
            <strong>Day 1</strong> — close thirds, A major, ascending and descending. LH on
            A pulse.
          </Step>
          <Step>
            <strong>Day 2</strong> — spread thirds, A major. Same direction sequence.
          </Step>
          <Step>
            <strong>Day 3</strong> — switch to A harmonic minor. Both close and spread.
          </Step>
          <Step>
            <strong>Day 4</strong> — build a rhythm pattern using just thirds. Pick three
            third-pairs and arrange them in any rhythm — make a riff.
          </Step>
          <Step>
            <strong>Day 5</strong> — improvise a melody using thirds only. Move freely
            between close and spread, ascending and descending. Sing along.
          </Step>
        </Steps>
      </Insight>

      <NextUp
        to="/lesson/piano-foundation-23"
        label="Lesson 23 · Pendulum Exercise"
        why="Thirds train the hand on small skips. The Pendulum trains the hand on long-range crossings — the thumb-under technique that lets your hand travel across the whole keyboard."
      />
    </LessonShell>
  );
}
